import { Areas, FlowStop as FlowStopType, SingularStationArea } from '@thive/fddm-types';
import { Stack } from '@mui/system';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { v4 as uuid } from 'uuid';

import { areasToFlatCollection, isRackingArea } from '@/modules/floorplanDesign/helpers';
import { Area } from '@/modules/floorplanDesign/types';
import { FlowStop } from './FlowStop';
import { FddmServiceFactory as FloorplanDesignServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { isAtLeast1 } from '@/modules/floorplanDesign/helpers/minItems';
import { generateNewGroupName } from '@/modules/floorplanDesign/helpers/name';
import { getLoadHandlingTypeLabel } from '@/modules/floorplanDesign/helpers/singularStationAreaHelpers';
import { NoFlowStops } from './NoFlowStops';
import { logger } from '@common/logging/logger';

type AreaItem = { value: string; label: string };

const getAreaLoadHandlingType = (area: Area): SingularStationArea['loadHandlingType'] | null => {
  if (isRackingArea(area)) return area.rackingLoadHandlingType;
  if ('loadHandlingType' in area) return area.loadHandlingType;

  return null;
};

const toAreaItem = (area: Area): AreaItem => ({ value: area.id, label: area.name });

interface Props {
  flowStops: FlowStopType[];
  selectedIds: string[];
  areas: Areas;
  showDraft: boolean;
  disabled?: boolean;
  onDismissDraft: () => void;
}

function FlowStopsComponent({
  flowStops,
  selectedIds,
  areas,
  showDraft,
  disabled = false,
  onDismissDraft,
}: Props) {
  const fddmSvc = useMemo(
    () => FloorplanDesignServiceFactory.getInstance().createFddmService(),
    [],
  );

  const [draftFlowStopId, setDraftFlowStopId] = useState<string | null>(null);

  useEffect(() => {
    // plz fix me
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (!showDraft) setDraftFlowStopId(null);
  }, [showDraft]);

  const allAreas = useMemo(() => areasToFlatCollection(areas), [areas]);
  const allAreaItems = useMemo(() => allAreas.map(toAreaItem), [allAreas]);

  const usedAreaIds = useMemo(
    () => new Set(flowStops.flatMap((flowStop) => flowStop.areaIds)),
    [flowStops],
  );

  const draftFlowStop = useMemo(
    () => flowStops.find((flowStop) => flowStop.id === draftFlowStopId) ?? null,
    [draftFlowStopId, flowStops],
  );

  const visibleFlowStops = useMemo(() => {
    const selectedSet = new Set(selectedIds);

    return flowStops.filter((flowStop) => {
      if (flowStop.id === draftFlowStopId) return false;
      if (selectedSet.size === 0) return true;

      return (
        selectedSet.has(flowStop.id) || flowStop.areaIds.some((areaId) => selectedSet.has(areaId))
      );
    });
  }, [draftFlowStopId, flowStops, selectedIds]);

  const handleSelectArea = useCallback(
    (flowStop: FlowStopType, areaId: string) => {
      if (disabled) return;

      fddmSvc.updateFlowStop({ ...flowStop, areaIds: [...flowStop.areaIds, areaId] });
    },
    [disabled, fddmSvc],
  );

  const handleRenameFlowStop = useCallback(
    (flowStop: FlowStopType, name: string) => {
      if (disabled) return;
      if (name === flowStop.name) return;

      fddmSvc.updateFlowStop({ ...flowStop, name });
    },
    [disabled, fddmSvc],
  );

  const handleRemoveFlowStop = useCallback(
    (flowStop: FlowStopType) => {
      if (disabled) return;

      fddmSvc.deleteFlowStops([flowStop]);

      if (flowStop.id === draftFlowStopId) {
        onDismissDraft();
      }
    },
    [disabled, draftFlowStopId, fddmSvc, onDismissDraft],
  );

  const handleRemoveArea = useCallback(
    (flowStop: FlowStopType, areaId: string) => {
      if (disabled) return;

      const updatedAreaIds = flowStop.areaIds.filter((id) => id !== areaId);

      if (!isAtLeast1(updatedAreaIds)) {
        handleRemoveFlowStop(flowStop);
        return;
      }

      fddmSvc.updateFlowStop({ ...flowStop, areaIds: updatedAreaIds });
    },
    [disabled, fddmSvc, handleRemoveFlowStop],
  );

  const getSelectableAreasFor = useCallback(
    (flowStop: FlowStopType): AreaItem[] => {
      const referenceArea = allAreas.find((area) => area.id === flowStop.areaIds[0]);
      const referenceLoadHandlingType = referenceArea
        ? getAreaLoadHandlingType(referenceArea)
        : null;

      if (!referenceArea || !referenceLoadHandlingType) {
        logger.error('No reference area / load handling type for flow stop', flowStop.id);
        return [];
      }

      return allAreas
        .filter(
          (area) =>
            !usedAreaIds.has(area.id) &&
            area.areaType === referenceArea.areaType &&
            getAreaLoadHandlingType(area) === referenceLoadHandlingType,
        )
        .map(toAreaItem);
    },
    [allAreas, usedAreaIds],
  );

  const getAreaTypeLabel = useCallback(
    (flowStop: FlowStopType): string | undefined => {
      const referenceArea = allAreas.find((area) => area.id === flowStop.areaIds[0]);
      const loadHandlingType = referenceArea ? getAreaLoadHandlingType(referenceArea) : null;

      return loadHandlingType ? getLoadHandlingTypeLabel(loadHandlingType) : undefined;
    },
    [allAreas],
  );

  const draftSelectableAreas = useMemo<AreaItem[]>(() => {
    if (draftFlowStop) return getSelectableAreasFor(draftFlowStop);

    return allAreas
      .filter((area) => !usedAreaIds.has(area.id) && getAreaLoadHandlingType(area) !== null)
      .map(toAreaItem);
  }, [allAreas, draftFlowStop, getSelectableAreasFor, usedAreaIds]);

  const handleCreateFromDraft = useCallback(
    (areaId: string) => {
      if (disabled) return;

      const newFlowStop: FlowStopType = {
        id: uuid(),
        name: generateNewGroupName('Flow Stop'),
        areaIds: [areaId],
      };

      setDraftFlowStopId(newFlowStop.id);
      fddmSvc.addFlowStops([newFlowStop]);
    },
    [disabled, fddmSvc],
  );

  const handleOverwriteFlowStop = useCallback(
    (flowStop: FlowStopType, areaId: string, previousAreaId: string) => {
      if (disabled) return;

      const updatedAreaIds = flowStop.areaIds.map((id) => (id === previousAreaId ? areaId : id));

      if (!isAtLeast1(updatedAreaIds)) {
        handleRemoveFlowStop(flowStop);
        return;
      }

      fddmSvc.updateFlowStop({ ...flowStop, areaIds: updatedAreaIds });
    },
    [disabled, fddmSvc, handleRemoveFlowStop],
  );

  return (
    <Stack gap={2}>
      {!showDraft && (
        <NoFlowStops
          allFlowStopsAmount={flowStops.length}
          relatedFlowStopsAmount={visibleFlowStops.length}
        />
      )}

      {visibleFlowStops.map((flowStop) => (
        <FlowStop
          key={flowStop.id}
          flowStop={flowStop}
          allAreaItems={allAreaItems}
          selectableAreaItems={getSelectableAreasFor(flowStop)}
          areaType={getAreaTypeLabel(flowStop)}
          disabled={disabled}
          onOverwriteArea={handleOverwriteFlowStop}
          onSelectArea={handleSelectArea}
          onRemoveArea={handleRemoveArea}
          onRemoveFlowStop={handleRemoveFlowStop}
          onRename={handleRenameFlowStop}
        />
      ))}

      {showDraft && (
        <FlowStop
          key='draft'
          flowStop={draftFlowStop}
          allAreaItems={allAreaItems}
          selectableAreaItems={draftSelectableAreas}
          areaType={draftFlowStop ? getAreaTypeLabel(draftFlowStop) : undefined}
          disabled={disabled}
          onSelectArea={handleSelectArea}
          onRemoveArea={handleRemoveArea}
          onRemoveFlowStop={handleRemoveFlowStop}
          onRename={handleRenameFlowStop}
          onCreate={handleCreateFromDraft}
          onDismissDraft={onDismissDraft}
        />
      )}
    </Stack>
  );
}

export const FlowStops = memo(FlowStopsComponent);
